import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Add as AddIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  AccountTree as GraphIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDatabase } from '../../hooks/useDatabase';
import { Initiative, Contact, Task } from '../../types/database';
import { InitiativeModal } from './InitiativeModal';
import { ContactModal } from '../contacts/ContactModal';
import { InitiativeRelationships } from './InitiativeRelationships';
import { TaskModal } from '../tasks/TaskModal';
import { ApiToggleButton } from '../common/ApiToggleButton';
import { InitiativeGoalScore } from '../../utils/initiativeGoalScoring';

export const InitiativeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const db = useDatabase();
  const [initiative, setInitiative] = useState<Initiative | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [allInitiatives, setAllInitiatives] = useState<Initiative[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [linkContactDialogOpen, setLinkContactDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [newContactModalOpen, setNewContactModalOpen] = useState(false);
  const [showAllContacts, setShowAllContacts] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTaskModalOpen, setNewTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [deleteTaskDialogOpen, setDeleteTaskDialogOpen] = useState(false);
  const [goalScores, setGoalScores] = useState<InitiativeGoalScore[]>([]);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Load initiative
        const { data: initiativeData, error: initiativeError } = await db.getInitiativeById(id);
        if (initiativeError) throw initiativeError;
        setInitiative(initiativeData);

        // Load all contacts for the linking dialog
        const { data: contactsData, error: contactsError } = await db.getContacts();
        if (contactsError) throw contactsError;
        setContacts(contactsData || []);

        // Load linked contacts by their IDs (in case they belong to a different user)
        if (initiativeData?.contacts && initiativeData.contacts.length > 0) {
          const { data: linkedContactsData, error: linkedContactsError } = await db.getContactsByIds(initiativeData.contacts);
          if (!linkedContactsError && linkedContactsData) {
            // Merge linked contacts with the contacts list, avoiding duplicates
            const existingIds = new Set((contactsData || []).map(c => c.id));
            const newContacts = linkedContactsData.filter(c => !existingIds.has(c.id));
            setContacts([...(contactsData || []), ...newContacts]);
          }
        }

        // Load all initiatives for navigation and sort alphabetically
        const { data: initiativesData, error: initiativesError } = await db.getInitiatives();
        if (initiativesError) throw initiativesError;
        const sortedInitiatives = (initiativesData || []).sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        setAllInitiatives(sortedInitiatives);

        // Load tasks for this initiative
        const { data: tasksData, error: tasksError } = await db.getTasksByInitiative(id);
        if (tasksError) throw tasksError;
        setTasks(tasksData || []);

        // Load scores from database instead of localStorage
        const { data: scores, error: scoresError } = await db.getInitiativeGoalScoresByInitiative(id);
        if (scoresError) {
          console.error('Error loading initiative goal scores:', scoresError);
        } else if (scores) {
          setGoalScores(scores);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, db]);

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!initiative) return;
    
    try {
      const { error } = await db.deleteInitiative(initiative.id);
      if (error) throw error;
      navigate('/initiatives');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete initiative');
    }
  };

  const handleEditSubmit = async (initiativeData: Omit<Initiative, 'id' | 'created_at' | 'updated_at'>) => {
    if (!initiative) return;

    try {
      const { error } = await db.updateInitiative(initiative.id, initiativeData);
      if (error) throw error;
      const { data } = await db.getInitiativeById(initiative.id);
      setInitiative(data);
      setEditModalOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update initiative');
    }
  };

  const handleLinkContact = async () => {
    if (!initiative || !selectedContact) return;

    try {
      const updatedContacts = [...initiative.contacts, selectedContact.id];
      const { error } = await db.updateInitiative(initiative.id, { contacts: updatedContacts });
      if (error) throw error;
      
      const { data } = await db.getInitiativeById(initiative.id);
      setInitiative(data);
      setLinkContactDialogOpen(false);
      setSelectedContact(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link contact');
    }
  };

  const handleUnlinkContact = async (contactId: string) => {
    if (!initiative) return;

    try {
      const updatedContacts = initiative.contacts.filter(id => id !== contactId);
      const { error } = await db.updateInitiative(initiative.id, { contacts: updatedContacts });
      if (error) throw error;
      
      const { data } = await db.getInitiativeById(initiative.id);
      setInitiative(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink contact');
    }
  };

  const handleCreateAndLinkContact = async (contactData: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at' | 'relevance_score'>>, organizationId?: string | null) => {
    if (!initiative) return;

    try {
      // Create new contact
      const { data: newContact, error: createError } = await db.createContact(contactData);
      if (createError) throw createError;
      if (!newContact) throw new Error('Failed to create contact');

      // Link organization if provided
      if (organizationId) {
        await db.linkContactToOrganization(newContact.id, organizationId);
      }

      // Link the new contact to the initiative
      const updatedContacts = [...initiative.contacts, newContact.id];
      const { error: linkError } = await db.updateInitiative(initiative.id, { contacts: updatedContacts });
      if (linkError) throw linkError;

      // Refresh data
      const { data: initiativeData } = await db.getInitiativeById(initiative.id);
      const { data: contactsData } = await db.getContacts();
      setInitiative(initiativeData);
      setContacts(contactsData || []);
      
      setNewContactModalOpen(false);
      setLinkContactDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create and link contact');
    }
  };

  const handleTaskSubmit = async (taskData: Omit<Task, 'id' | 'created_at' | 'updated_at'>) => {
    if (!initiative) return;

    try {
      if (selectedTask) {
        // Update existing task
        const { error } = await db.updateTask(selectedTask.id, taskData);
        if (error) throw error;
      } else {
        // Create new task
        const { error } = await db.createTask({
          ...taskData,
          entity_type: 'initiative',
          entity_id: initiative.id
        });
        if (error) throw error;
      }

      // Refresh tasks
      const { data: tasksData } = await db.getTasksByInitiative(initiative.id);
      setTasks(tasksData || []);
      
      setNewTaskModalOpen(false);
      setSelectedTask(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task');
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask || !initiative) return;

    try {
      const { error } = await db.deleteTask(selectedTask.id);
      if (error) throw error;

      // Refresh tasks
      const { data: tasksData } = await db.getTasksByInitiative(initiative.id);
      setTasks(tasksData || []);
      
      setDeleteTaskDialogOpen(false);
      setSelectedTask(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  };

  const handleApiToggle = async () => {
    if (!initiative) return;
    
    try {
      const newApiState = !initiative.use_api_data;
      const { error } = await db.updateInitiative(initiative.id, { use_api_data: newApiState });
      if (error) throw error;
      
      // Refresh initiative data from database to ensure consistency
      const { data: updatedInitiative, error: fetchError } = await db.getInitiativeById(initiative.id);
      if (fetchError) throw fetchError;
      
      // Update local state with fresh data
      setInitiative(updatedInitiative);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update API data setting');
    }
  };

  // Get current initiative index and navigation functions
  const currentIndex = initiative ? allInitiatives.findIndex(i => i.id === initiative.id) : -1;
  const hasNext = currentIndex < allInitiatives.length - 1;
  const hasPrevious = currentIndex > 0;

  const handleNext = () => {
    if (hasNext && currentIndex !== -1) {
      const nextInitiative = allInitiatives[currentIndex + 1];
      navigate(`/initiatives/${nextInitiative.id}`);
    }
  };

  const handlePrevious = () => {
    if (hasPrevious && currentIndex !== -1) {
      const prevInitiative = allInitiatives[currentIndex - 1];
      navigate(`/initiatives/${prevInitiative.id}`);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  if (!initiative) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 2 }}>
          <Alert severity="warning">Initiative not found</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  // Get linked contacts
  const linkedContacts = contacts.filter(contact => initiative.contacts.includes(contact.id));
  // Get available contacts (not linked)
  const availableContacts = contacts.filter(contact => !initiative.contacts.includes(contact.id));

  // Function to control how many contacts are shown
  const displayedContacts = () => {
    return showAllContacts ? linkedContacts : linkedContacts.slice(0, 5);
  };

  return (
    <DashboardLayout>
      <Box sx={{ 
        flexGrow: 1, 
        p: { xs: 1, sm: 2 },
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              mb: 2,
              gap: 1
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={() => navigate('/initiatives')}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Box sx={{ 
                    display: { xs: 'none', sm: 'flex' },
                    gap: 1
                  }}>
                    <IconButton
                      onClick={handlePrevious}
                      disabled={!hasPrevious}
                      size="small"
                    >
                      <NavigateBeforeIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleNext}
                      disabled={!hasNext}
                      size="small"
                    >
                      <NavigateNextIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={handleEdit}
                    size="small"
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => navigate(`/graph?isolate=${initiative.id}`)}
                    size="small"
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText'
                      }
                    }}
                  >
                    <GraphIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={handleDelete}
                    size="small"
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'error.main',
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'error.contrastText'
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ 
                display: { xs: 'flex', sm: 'none' },
                gap: 1, 
                justifyContent: 'center'
              }}>
                <IconButton
                  onClick={handlePrevious}
                  disabled={!hasPrevious}
                  size="small"
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton
                  onClick={handleNext}
                  disabled={!hasNext}
                  size="small"
                >
                  <NavigateNextIcon />
                </IconButton>
              </Box>
            </Box>
          </Grid>

          {/* Initiative Info Card */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  {initiative.name}
                </Typography>
                <Chip
                  label={initiative.status}
                  sx={{
                    mb: 2,
                    bgcolor: 
                      initiative.status === 'Active' ? 'rgba(76, 175, 80, 0.1)' :
                      initiative.status === 'Completed' ? 'rgba(158, 158, 158, 0.1)' :
                      'rgba(255, 152, 0, 0.1)',
                    color: 
                      initiative.status === 'Active' ? 'rgba(76, 175, 80, 0.9)' :
                      initiative.status === 'Completed' ? 'rgba(158, 158, 158, 0.9)' :
                      'rgba(255, 152, 0, 0.9)',
                    border: '1px solid',
                    borderColor: 
                      initiative.status === 'Active' ? 'rgba(76, 175, 80, 0.3)' :
                      initiative.status === 'Completed' ? 'rgba(158, 158, 158, 0.3)' :
                      'rgba(255, 152, 0, 0.3)',
                  }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <List>
                <ListItem sx={{ pl: 2 }}>
                  <ListItemIcon>
                    <CalendarIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Timeline"
                    secondary={
                      (!initiative.start_date || !initiative.end_date ||
                        ["1970-01-01", "0001-01-01"].includes(initiative.start_date.slice(0, 10)) ||
                        ["1970-01-01", "0001-01-01"].includes(initiative.end_date.slice(0, 10)))
                        ? 'No date was set'
                        : `${new Date(initiative.start_date).toLocaleDateString()} - ${new Date(initiative.end_date).toLocaleDateString()}`
                    }
                  />
                </ListItem>
                <ListItem sx={{ pl: 2 }}>
                  <ListItemIcon>
                    <DescriptionIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Description"
                    secondary={initiative.description || 'No description provided'}
                    sx={{
                      '& .MuiListItemText-secondary': {
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%'
                      }
                    }}
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <ApiToggleButton
                  isActive={initiative.use_api_data || false}
                  onToggle={handleApiToggle}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Contacts Section */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Linked Contacts</Typography>
                <Button
                  startIcon={<LinkIcon />}
                  onClick={() => setLinkContactDialogOpen(true)}
                >
                  Link Contact
                </Button>
              </Box>
              {linkedContacts.length > 0 ? (
                <>
                  <List>
                    {displayedContacts().map((contact) => (
                      <ListItem
                        key={contact.id}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={() => handleUnlinkContact(contact.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <Avatar 
                            sx={{ 
                              bgcolor: 'primary.main',
                              cursor: 'pointer'
                            }}
                            onClick={() => navigate(`/contacts/${contact.id}`)}
                          >
                            <PersonIcon />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={contact.name}
                          secondary={contact.email}
                          onClick={() => navigate(`/contacts/${contact.id}`)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {linkedContacts.length > 5 && (
                    <Button
                      onClick={() => setShowAllContacts(!showAllContacts)}
                      sx={{ mt: 1 }}
                      size="small"
                    >
                      {showAllContacts ? 'Show Less' : `Show ${linkedContacts.length - 5} More`}
                    </Button>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No contacts linked to this initiative
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Tasks Section */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Tasks</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setNewTaskModalOpen(true)}
                  variant="contained"
                >
                  Add Task
                </Button>
              </Box>
              {tasks.length > 0 ? (
                <List>
                  {tasks.map((task) => (
                    <ListItem
                      key={task.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: 
                              task.status === 'completed' ? 'success.main' :
                              task.status === 'in_progress' ? 'warning.main' :
                              task.status === 'pending' ? 'info.main' :
                              'grey.500'
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={task.title}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {task.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={task.status}
                                size="small"
                                sx={{
                                  bgcolor: 
                                    task.status === 'completed' ? 'success.light' :
                                    task.status === 'in_progress' ? 'warning.light' :
                                    task.status === 'pending' ? 'info.light' :
                                    'grey.300',
                                  color: 
                                    task.status === 'completed' ? 'success.dark' :
                                    task.status === 'in_progress' ? 'warning.dark' :
                                    task.status === 'pending' ? 'info.dark' :
                                    'grey.700'
                                }}
                              />
                              {task.due_date && (
                                <Chip
                                  label={`Due: ${new Date(task.due_date).toLocaleDateString()}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {task.priority && (
                                <Chip
                                  label={`Priority: ${task.priority}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: 
                                      task.priority === 'high' ? 'error.main' :
                                      task.priority === 'medium' ? 'warning.main' :
                                      'info.main',
                                    color: 
                                      task.priority === 'high' ? 'error.main' :
                                      task.priority === 'medium' ? 'warning.main' :
                                      'info.main'
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                        sx={{
                          '& .MuiListItemText-secondary': {
                            width: '100%'
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedTask(task);
                            setNewTaskModalOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedTask(task);
                            setDeleteTaskDialogOpen(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No tasks created for this initiative
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Initiative Relationships Section */}
          <Grid item xs={12} md={8}>
            <InitiativeRelationships
              initiative={initiative}
              onUpdate={(updatedInitiative) => {
                setInitiative(updatedInitiative);
                // Refresh all initiatives to update the list
                const loadAllInitiatives = async () => {
                  const { data: initiativesData } = await db.getInitiatives();
                  setAllInitiatives(initiativesData || []);
                };
                loadAllInitiatives();
              }}
            />
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Initiative</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete {initiative.name}? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Link Contact Dialog */}
        <Dialog
          open={linkContactDialogOpen}
          onClose={() => {
            setLinkContactDialogOpen(false);
            setSelectedContact(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              minHeight: '60vh',
              maxHeight: '80vh'
            }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Link Contact</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Search and select a contact to link to this initiative
                </Typography>
              </Box>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setNewContactModalOpen(true)}
                variant="outlined"
              >
                Create New Contact
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                options={availableContacts}
                getOptionLabel={(option) => option.name}
                value={selectedContact}
                onChange={(_, newValue) => setSelectedContact(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Contacts"
                    margin="normal"
                    fullWidth
                    autoFocus
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 1 }}>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email}
                      </Typography>
                      {option.industry && (
                        <Typography variant="caption" color="text.secondary">
                          Industry: {option.industry}
                        </Typography>
                      )}
                      {option.skills && option.skills.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {option.skills.map((skill) => (
                            <Chip
                              key={skill}
                              label={skill}
                              size="small"
                              sx={{ height: 20, fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </li>
                )}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(
                    (option) =>
                      (option.name?.toLowerCase() || '').includes(searchTerm) ||
                      (option.email?.toLowerCase() || '').includes(searchTerm) ||
                      (option.industry?.toLowerCase() || '').includes(searchTerm) ||
                      (option.industries?.some(i => (i?.toLowerCase() || '').includes(searchTerm)) || false) ||
                      (option.skills?.some(s => (s?.toLowerCase() || '').includes(searchTerm)) || false)
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No contacts found"
                loadingText="Loading contacts..."
                openText="Open"
                closeText="Close"
                clearText="Clear"
                ListboxProps={{
                  sx: {
                    maxHeight: '50vh'
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => {
                setLinkContactDialogOpen(false);
                setSelectedContact(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLinkContact}
              variant="contained"
              disabled={!selectedContact}
            >
              Link Contact
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Contact Modal */}
        <ContactModal
          open={newContactModalOpen}
          onClose={() => setNewContactModalOpen(false)}
          onSubmit={handleCreateAndLinkContact}
        />

        {/* Edit Modal */}
        <InitiativeModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSubmit={handleEditSubmit}
          initiative={initiative}
        />

        {/* Task Modal */}
        <TaskModal
          open={newTaskModalOpen}
          onClose={() => {
            setNewTaskModalOpen(false);
            setSelectedTask(null);
          }}
          onSubmit={handleTaskSubmit}
          task={selectedTask}
          prefillInitiative={initiative.id}
        />

        {/* Delete Task Confirmation Dialog */}
        <Dialog
          open={deleteTaskDialogOpen}
          onClose={() => setDeleteTaskDialogOpen(false)}
        >
          <DialogTitle>Delete Task</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{selectedTask?.title}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTaskDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteTask}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Goal Scores Section - Moved to end of page */}
        {goalScores.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Goal Alignment Scores
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                How well this initiative advances your goals (scores out of 100)
              </Typography>
              <Grid container spacing={2}>
                {goalScores
                  .sort((a, b) => {
                    // First sort by category order: personal, professional, financial
                    const categoryOrder = { personal: 0, professional: 1, financial: 2 };
                    const categoryDiff = categoryOrder[a.goalCategory as keyof typeof categoryOrder] - categoryOrder[b.goalCategory as keyof typeof categoryOrder];
                    if (categoryDiff !== 0) return categoryDiff;
                    // Then sort by score (highest first) within each category
                    return b.score - a.score;
                  })
                  .map((score) => (
                    <Grid item xs={12} sm={6} md={4} key={`${score.initiativeId}-${score.goalId}`}>
                      <Paper 
                        sx={{ 
                          p: 2, 
                          border: '1px solid',
                          borderColor: score.score >= 70 ? 'success.main' : 
                                       score.score >= 40 ? 'warning.main' : 'error.main',
                          bgcolor: score.score >= 70 ? 'rgba(76, 175, 80, 0.05)' : 
                                  score.score >= 40 ? 'rgba(255, 152, 0, 0.05)' : 'rgba(244, 67, 54, 0.05)'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {score.goalTitle}
                          </Typography>
                          <Chip
                            label={`${score.score}/100`}
                            size="small"
                            sx={{
                              bgcolor: score.score >= 70 ? 'success.main' : 
                                      score.score >= 40 ? 'warning.main' : 'error.main',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {score.goalCategory} Goal
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem' }}>
                          {score.reasoning}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
              </Grid>
            </Paper>
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
}; 